import React, { useEffect } from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import useMicroFrontendUrlFromManifest from '@fd/customHooks/useMicroFrontendFromManifest';
import CircularProgress from '@mui/material/CircularProgress';

import { getMfeOverrideUrl } from '../../components/FlipdishToolbar/utils';
import { rmsBaseUrl } from '../../components/RMS/rms.services';
import { getApiEndPoint } from '../../helpers/apibase';
import { processMfeQueryParams } from '../../helpers/mfeQueryParams';
import { isProductionPortalHost } from '../../helpers/portalHost';
import { permissionsSelector } from '../../selectors/permissions.selector';
import { logger } from '../../services/loggerService';

type MicroFrontendProps = {
  enableManifestFile?: boolean; // Whether to enable the manifest file.
  partialIndexUrl?: string; // The partial path to the microfrontend script in cloudfront, should be set if manifest file is enabled (modern for caching optimisation).
  indexUrl?: string; // The full url of the microfrontend script in cloudfront (legacy).
  microFrontendWindowName: string; // The name of the microfrontend window object (set in main.tsx of the MF).
  language: string; // The current portal language.
  portalBaseUrl: string; // The base URL of the portal so that the MF ignores the base.
  otherProps?: { [key: string]: string | boolean | number }; // Additional data props (treated as string, prefix with "data-").
  className?: string; // The class name of the microfrontend container.
};

/**
 * MicroFrontend component.
 *
 * Only use 1 Micro Frontend per Portal page!
 */
const MicroFrontend = (props: MicroFrontendProps): JSX.Element => {
  const currentApp = useAppSelector((state) => state.currentApp);
  const isFlipdishStaff = useAppSelector((state) => {
    const getPermissionsSelector = permissionsSelector.hasPermissionFactory(['FlipdishStaff']);
    return getPermissionsSelector(state);
  });

  const {
    indexUrl: legacyIndexUrl = '',
    microFrontendWindowName,
    language,
    portalBaseUrl,
    enableManifestFile = false,
    partialIndexUrl = '',
    otherProps = {},
    className = '',
  } = props;
  const isProd = isProductionPortalHost();

  let resolvedLegacyIndexUrl = legacyIndexUrl;
  let resolvedPartialIndexUrl = partialIndexUrl;
  let resolvedEnableManifestFile = enableManifestFile;

  if (!isProd) {
    processMfeQueryParams();
    const overrideUrl = getMfeOverrideUrl(microFrontendWindowName);
    if (overrideUrl) {
      resolvedLegacyIndexUrl = overrideUrl;
      resolvedPartialIndexUrl = '';
      resolvedEnableManifestFile = false;
    }
  }

  const appId = currentApp?.AppId ?? '';
  const orgId = currentApp?.OrgId ?? '';
  const brandId = currentApp?.AppId ?? '';

  const { indexUrl, error } = useMicroFrontendUrlFromManifest({
    partialIndexUrl: resolvedPartialIndexUrl,
    enableManifestFile: resolvedEnableManifestFile,
    legacyIndexUrl: resolvedLegacyIndexUrl,
    microFrontendWindowName,
  });

  useEffect(() => {
    if (error?.message) {
      throw error;
    }
  }, [error?.message]);

  useEffect(() => {
    const scriptId = `${microFrontendWindowName}-entry-script`;

    const renderMicroFrontend = () => {
      let existingScript = document.querySelector(`script[id="${scriptId}"]`);
      const existingScriptSrc = existingScript?.getAttribute('src');
      // if the script is already loaded and MFE has been re-deployed (indexUrl has changed), remove the script and the window object
      if (existingScriptSrc && indexUrl && existingScriptSrc !== indexUrl) {
        existingScript?.remove();
        window[microFrontendWindowName] = null;
        existingScript = null;
      }

      if (!window[microFrontendWindowName]) {
        const mfScriptUrl = indexUrl;
        // edge case where the script loads twice
        if (!existingScript) {
          // first render no script is loaded
          const script = document.createElement('script');
          script.src = mfScriptUrl;
          script.type = 'module';
          script.id = scriptId;
          document.body.appendChild(script);
        }
      } else {
        const microFrontend = window[microFrontendWindowName];
        if (microFrontend) {
          // subsequent renders to save reloading assets from CDN
          microFrontend.renderMicroFrontend();
        } else {
          logger.error(`Micro Frontend ${microFrontendWindowName} not found`);
        }
      }
    };

    indexUrl && renderMicroFrontend();

    return () => {
      window[microFrontendWindowName]?.unMountMicroFrontend();
    };
  }, [indexUrl, microFrontendWindowName]);

  if (!indexUrl) {
    return <CircularProgress />;
  }

  return (
    <div
      id="flipdish-micro-frontend"
      data-language={language}
      data-portal-base-url={portalBaseUrl}
      data-zeus-base-url={getApiEndPoint()}
      data-rms-base-url={rmsBaseUrl}
      data-org-id={orgId}
      data-app-id={appId}
      data-brand-id={brandId}
      data-is-flipdish-staff={isFlipdishStaff}
      // Support for pre @flipdish/portal-library@5.3.0
      data-appId={appId}
      data-brandId={brandId}
      data-orgId={orgId}
      data-isFlipdishStaff={isFlipdishStaff}
      data-appid={appId}
      data-brandid={brandId}
      data-orgid={orgId}
      data-isflipdishstaff={isFlipdishStaff}
      className={className}
      {...otherProps}
    />
  );
};

export default MicroFrontend;
